//* Importa hooks do React
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuestions } from "../../../hooks/questions/useQuestions";
import { useDisciplines } from "../../../hooks/disciplines/useDisciplines";
import { useQuestionTag } from "../../../hooks/questionsTags/useQuestionTag";

//* Importa componentes do Bootstrap 
import {
  Form,
  InputGroup,
  Button,
  Table,
  Card,
  Container,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";

//! Importing Jodit Editor
import JoditEditor from "jodit-react";

//? Importa ícones
import { SaveRounded } from "@material-ui/icons";
import { FaPlusSquare } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { BsFillEyeFill } from "react-icons/bs";

//todo Importing components
import CustomAlert from "../../../component/base/CustomAlert";
import { TagsSelector } from "../../../component/common/TagsSelector";
import { SetSubQuestionModal } from "../form/components/SubQuestionModal";
import QuestionModal from "../form/components/ModalQuestionExist";
import { ErrorMessages } from "../../../component/common/ErrorMessages";

//? Importa types
import {
  QuestionEducationLevel,
  QuestionDifficultyLevel,
  QuestionType,
  QuestionDetails,
  UpdateQuestionInput,
  Question,
} from "../../../hooks/questions/types/types";
import {
  ObjectiveAlternative,
  LocalSubQuestion,
  OldTag,
} from "../form/types/types";

//! Importa constants
import { API_URI } from "../../../constants/consts";
import { api } from "../../../services/apis/configs/axiosConfigs";
import {
  mapQuestionPayload,
  mapQuestionResponse,
} from "../../../hooks/questions/questionsAPI";
import { defaultEditorConfig } from "../../../constants/configJodit";
import { useSanitizeInput } from "../../../constants/configJodit";

/**
 * Componente para criação ou edição de questões.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {boolean} [props.editMode] - Indica se o formulário está no modo de edição.
 *
 * @returns {JSX.Element} O componente renderizado.
 *
 * @component
 *
 * @example
 * <QuestionsForm editMode={true} />
 */
export function QuestionsForm({ editMode }: { editMode?: boolean }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const token = useSelector((state: any) => state.auth.token);

  // Hooks
  const { discipline } = useDisciplines(token);
  const { createRelations, deleteRelations } = useQuestionTag(token);
  const { createQuestion, getQuestionById, isCreating, isUpdating } =
    useQuestions(token);

  // Estados do formulário
  const [disciplina, setDisciplina] = useState<string>("");
  const [nivelEscolaridade, setNivelEscolaridade] =
    useState<QuestionEducationLevel>("elementary");
  const [nivelDificuldade, setNivelDificuldade] =
    useState<QuestionDifficultyLevel>("easy");

  // Tags
  const [tags, setTags] = useState<string[]>([]);
  const [oldTags, setOldTags] = useState<OldTag[]>([]);

  // Conteúdo dos editores
  const [enunciado, setEnunciado] = useState<string>("");
  const [justificativa, setJustificativa] = useState<string>("");

  // Estados para erros de validação
  const [enunciadoError, setEnunciadoError] = useState<string>("");
  const [justificativaError, setJustificativaError] = useState<string>("");
  const [altError, setAltError] = useState<string>("");

  // Tipo da questão
  const [tipoQuestao, setTipoQuestao] = useState<QuestionType>("objective");

  // Alternativas para questões objetivas
  const [alternativas, setAlternativas] = useState<ObjectiveAlternative[]>([]);

  // Estado para escolher quantas alternativas adicionar
  const [alternativeCount, setAlternativeCount] = useState<number>(1);

  // Subquestões
  const [subQuestions, setSubQuestions] = useState<LocalSubQuestion[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalQuestion, setShowModalQuestion] = useState<boolean>(false);
  const [editingSubQ, setEditingSubQ] = useState<LocalSubQuestion | null>(null);

  // Estados para erros gerais e flag de tentativa de salvar
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [attemptedSave, setAttemptedSave] = useState<boolean>(false);
  const clearErrorMessages = useCallback(() => setErrorMessages([]), []);

  // Estado para armazenar o payload (em caso de erro 409)
  const [payloadState, setPayloadState] = useState<any>(null);

  // Estado de loading
  const [loading, setLoading] = useState<boolean>(false);

  // Refs para os editores
  const enunciadoEditorRef = useRef<any>(null);
  const justificativaEditorRef = useRef<any>(null);

  // Ref para armazenar o payload original (para comparação na edição)
  const originalPayloadRef = useRef<any>(null);

  // Validação dos campos obrigatórios (global)
  const validateFields = useCallback((): boolean => {
    const missing: string[] = [];
    if (!disciplina) missing.push("disciplina");
    if (!nivelEscolaridade) missing.push("nivelEscolaridade");
    if (!nivelDificuldade) missing.push("nivelDificuldade");
    if (!enunciado) missing.push("enunciado");
    if (tipoQuestao === "objective" && alternativas.length === 0)
      missing.push("alternativas");
    if (tipoQuestao === "set" && subQuestions.length === 0)
      missing.push("subQuestions");
    return missing.length === 0;
  }, [
    disciplina,
    nivelEscolaridade,
    nivelDificuldade,
    enunciado,
    tipoQuestao,
    alternativas,
    subQuestions,
  ]);

  const { sanitizeInput } = useSanitizeInput();

  // Validação em tempo real para o enunciado
  useEffect(() => {
    if (!enunciado) {
      setEnunciadoError("Enunciado é obrigatório.");
    } else {
      setEnunciadoError("");
    }
  }, [enunciado]);

  // Validação em tempo real para a justificativa
  useEffect(() => {
    if (tipoQuestao === "set") {
      // No modo "Texto em Comum" a justificativa não é utilizada na questão principal.
      setJustificativaError("");
    } else {
      if (!justificativa) {
        setJustificativaError(
          "Justificativa é obrigatória para este tipo de questão."
        );
      } else {
        setJustificativaError("");
      }
    }
  }, [justificativa, tipoQuestao]);

  // Validação em tempo real para alternativas: deve haver exatamente uma alternativa correta
  useEffect(() => {
    if (tipoQuestao === "objective") {
      const correctCount = alternativas.filter((alt) => alt.isCorrect).length;
      if (alternativas.length > 0 && correctCount !== 1) {
        setAltError(
          "Exatamente uma alternativa deve ser marcada como correta."
        );
      } else {
        setAltError("");
      }
    } else {
      setAltError("");
    }
  }, [alternativas, tipoQuestao]);

  // Mapeia uma subquestão para o payload
  const mapSubQuestionForPayload = useCallback((sq: LocalSubQuestion) => {
    const subQuestionPayload: any = {
      statement: sq.statement,
      answer_justification: sq.answerJustification,
      type: sq.type,
    };
    if (sq.id) subQuestionPayload.id = sq.id;
    if (sq.type === "objective" && sq.objectives) {
      subQuestionPayload.objectives = sq.objectives.map(
        (obj: any, index: any) => {
          const objPayload: any = {
            text: obj.text,
            is_correct: obj.isCorrect,
            order: index + 1,
          };
          if (obj.id) objPayload.id = obj.id;
          return objPayload;
        }
      );
    }
    return subQuestionPayload;
  }, []);

  // Função para construir o payload da questão
  const buildPayload = useCallback(() => {
    const payload: any = {
      discipline_id: disciplina,
      education_level: nivelEscolaridade,
      difficulty: nivelDificuldade,
    };

    if (tipoQuestao === "objective") {
      payload.sub_questions = [
        mapSubQuestionForPayload({
          localId: subQuestions[0]?.localId || crypto.randomUUID(),
          id: subQuestions[0]?.id,
          type: "objective",
          statement: enunciado,
          answerJustification: justificativa,
          objectives: alternativas,
        }),
      ];
    } else if (tipoQuestao === "dissertative") {
      payload.sub_questions = [
        mapSubQuestionForPayload({
          localId: subQuestions[0]?.localId || crypto.randomUUID(),
          id: subQuestions[0]?.id,
          type: "dissertative",
          statement: enunciado,
          answerJustification: justificativa,
        }),
      ];
    } else if (tipoQuestao === "set") {
      payload.statement = enunciado;
      payload.sub_questions = subQuestions.map(mapSubQuestionForPayload);
    }
    return payload;
  }, [
    disciplina,
    nivelEscolaridade,
    nivelDificuldade,
    enunciado,
    justificativa,
    tipoQuestao,
    subQuestions,
    alternativas,
    mapSubQuestionForPayload,
  ]);

  // Ao carregar uma questão para edição, captura os dados originais
  useEffect(() => {
    if (editMode && !loading && !originalPayloadRef.current) {
      originalPayloadRef.current = buildPayload();
    }
  }, [editMode, loading, buildPayload]);

  // ***** FUNÇÃO DE UPDATE CUSTOM (MAPEANDO O ERRO 409) *****
  const updateQuestionCustom = useCallback(
    async (id: string, data: UpdateQuestionInput): Promise<Question> => {
      const url = `${API_URI}/admin/private/questions/${id}`;
      const payload = mapQuestionPayload(data);
      try {
        const response = await api.request({
          url,
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: payload,
        });
        return mapQuestionResponse(response.data.data);
      } catch (error: any) {
        console.error("Error updating Question:", error);
        if (error.status === 409) {
          setShowModalQuestion(true);
          throw new Error("Questão em uso. Deseja criar uma nova?");
        }
        throw new Error("Error updating Question");
      }
    },
    [token]
  );
  // ***** FIM DA FUNÇÃO UPDATE CUSTOM *****

  // Função para salvar ou atualizar a questão
  const handleSaveAll = useCallback(async () => {
    setAttemptedSave(true);

    // Se estiver em edição, verifica se houve alteração nos dados principais e nas tags.
    if (editMode && id && originalPayloadRef.current) {
      const newPayload = buildPayload();
      const originalPayloadStr = JSON.stringify(originalPayloadRef.current);
      const newPayloadStr = JSON.stringify(newPayload);
      const originalTagsStr = JSON.stringify(oldTags.map((t) => t.id));
      const newTagsStr = JSON.stringify(tags);
      if (
        originalPayloadStr === newPayloadStr &&
        originalTagsStr === newTagsStr
      ) {
        navigate("/questions");
        return;
      }
    }

    if (!validateFields() || enunciadoError || justificativaError || altError) {
      setErrorMessages([
        "Existem campos obrigatórios não preenchidos ou com erros.",
      ]);
      return;
    }

    try {
      let questionId: string | null = null;
      const payload = buildPayload();
      setPayloadState(payload);

      if (id) {
        await updateQuestionCustom(id, payload);
        questionId = id;
      } else {
        const newQuestion = await createQuestion(payload);
        questionId = newQuestion.id;
      }

      // Atualiza as tags (associações)
      if (questionId) {
        const newTagIds = tags;
        const oldTagIds = oldTags.map((t) => t.id);
        const tagsToCreate = newTagIds
          .filter((tagId) => !oldTagIds.includes(tagId))
          .map((tagId) => ({
            question_id: questionId,
            tag_id: tagId,
          }));
        const tagsToDelete = oldTags
          .filter((t) => !newTagIds.includes(t.id) && t.questionTagId)
          .map((t) => t.questionTagId as string);

        if (tagsToCreate.length > 0)
          await createRelations({ associations: tagsToCreate });
        if (tagsToDelete.length > 0) await deleteRelations(tagsToDelete);
      }
      navigate("/questions");
    } catch (error: any) {
      console.error("Erro ao salvar questão:", error);
      setErrorMessages([error.message || "Erro ao salvar. Tente novamente."]);
    }
  }, [
    validateFields,
    buildPayload,
    id,
    createQuestion,
    updateQuestionCustom,
    tags,
    oldTags,
    createRelations,
    deleteRelations,
    navigate,
    enunciadoError,
    justificativaError,
    altError,
    editMode,
  ]);

  // Função chamada quando o usuário confirma criar uma nova questão (erro 409)
  const handleConfirmCreate = useCallback(async () => {
    try {
      if (!payloadState) {
        setErrorMessages(["Dados da questão não encontrados."]);
        return;
      }
      const newQuestion = await createQuestion(payloadState);
      const questionId = newQuestion.id;

      if (tags.length > 0) {
        const associations = tags.map((tagId) => ({
          question_id: questionId,
          tag_id: tagId,
        }));
        await createRelations({ associations });
      }
      navigate("/questions");
    } catch (error) {
      console.error("Erro ao criar nova questão:", error);
      setErrorMessages(["Erro ao criar a nova questão. Tente novamente."]);
    } finally {
      setShowModalQuestion(false);
    }
  }, [payloadState, createQuestion, tags, createRelations, navigate]);

  // Função para fechar o modal de questão existente
  const handleCancelModal = useCallback(() => {
    setShowModalQuestion(false);
  }, []);

  // Carrega os dados da questão para edição (com spinner enquanto carrega)
  useEffect(() => {
    const fetchQuestion = async () => {
      if (editMode && id) {
        setLoading(true);
        const question: QuestionDetails | null = await getQuestionById(id);
        setLoading(false);
        if (!question) return;

        setDisciplina(question.discipline.id);
        setNivelEscolaridade(question.educationLevel as QuestionEducationLevel);
        setNivelDificuldade(question.difficulty as QuestionDifficultyLevel);

        const originalTags = (question.tags || []).map((t) => ({
          id: t.id,
          questionTagId: t.questionTagId,
          name: t.name,
          color: t.color,
        }));
        setOldTags(originalTags);
        setTags(originalTags.map((t) => t.id));

        if (question.statement) {
          // Modo "Texto em Comum"
          setTipoQuestao("set");
          setEnunciado(String(question.statement));
          setSubQuestions(
            question.subQuestions
              .filter((sq) => sq.type !== "set")
              .map((sq) => ({
                localId: sq.id,
                id: sq.id,
                type: sq.type as "objective" | "dissertative",
                statement: String(sq.statement),
                answerJustification: String(sq.answerJustification) || "",
                objectives: sq.objectives?.map((obj) => ({
                  localId: obj.id,
                  id: obj.id,
                  text: String(obj.text),
                  isCorrect: obj.isCorrect,
                })),
              }))
          );
        } else if (question.subQuestions.length > 0) {
          const firstSub = question.subQuestions[0];
          setTipoQuestao(firstSub.type);
          setEnunciado(String(firstSub.statement));
          setJustificativa(String(firstSub.answerJustification) || "");
          setSubQuestions([
            {
              localId: firstSub.id,
              id: firstSub.id,
              type: firstSub.type as "objective" | "dissertative",
              statement: String(firstSub.statement),
              answerJustification: String(firstSub.answerJustification) || "",
              objectives: firstSub.objectives?.map((obj) => ({
                localId: obj.id,
                id: obj.id,
                text: String(obj.text),
                isCorrect: obj.isCorrect,
              })),
            },
          ]);
          if (firstSub.type === "objective" && firstSub.objectives) {
            setAlternativas(
              firstSub.objectives.map((obj) => ({
                localId: obj.id,
                id: obj.id,
                text: String(obj.text),
                isCorrect: obj.isCorrect,
              }))
            );
          }
        }
      }
    };
    fetchQuestion();
  }, [id, getQuestionById, editMode]);

  // Handler para adicionar alternativa
  const handleAddMultipleAlternatives = useCallback((count: number) => {
    const newAlternatives = Array.from({ length: count }, () => ({
      localId: crypto.randomUUID(),
      text: "",
      isCorrect: false,
    }));
    setAlternativas((prev) => [...prev, ...newAlternatives]);
  }, []);

  const handleRemoveAlternative = useCallback((localId: string) => {
    setAlternativas((prev) => prev.filter((alt) => alt.localId !== localId));
  }, []);

  // Handlers para subquestões
  const handleOpenModal = useCallback(() => {
    setEditingSubQ(null);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleSaveSubQuestion = useCallback(
    (data: LocalSubQuestion) => {
      setSubQuestions((prev) =>
        editingSubQ
          ? prev.map((sq) =>
              sq.localId === editingSubQ.localId ? { ...sq, ...data } : sq
            )
          : [...prev, data]
      );
      setShowModal(false);
      setEditingSubQ(null);
    },
    [editingSubQ]
  );

  const handleEditSubQuestion = useCallback((subQ: LocalSubQuestion) => {
    setEditingSubQ(subQ);
    setShowModal(true);
  }, []);

  const handleDeleteSubQuestion = useCallback((localId: string) => {
    setSubQuestions((prev) => prev.filter((sq) => sq.localId !== localId));
  }, []);

  // Função para converter o índice em letra (a), b), c), etc.)
  const indexToLetter = (index: number): string => {
    return String.fromCharCode(97 + index) + ")";
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <div className="py-5 px-3">
          {attemptedSave && errorMessages.length > 0 && (
            <div className="text-center mb-3">
              <ErrorMessages
                messages={errorMessages}
                clearErrors={clearErrorMessages}
              />
            </div>
          )}

          <div className="card-body" id="card">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4
                  className="mt-0 header-title"
                  style={{ fontSize: "1.5rem", color: "black" }}
                >
                  {id ? "Editar Questão" : "Cadastrar Questão"}
                </h4>
                <p style={{ fontSize: "1rem", color: "black" }}>
                  {id
                    ? "Edite os dados da questão."
                    : "Crie sua questão no sistema."}
                </p>
              </div>
            </div>
            <br />
            <Form style={{ position: "relative" }}>
              {/* Campo "Tipo de Questão" - desabilitado no modo de edição */}
              <InputGroup>
                <div className="flex fdcolumn flex1 margin-bottom">
                  <Form.Label>Tipo de Questão</Form.Label>
                  <Form.Select
                    value={tipoQuestao}
                    onChange={(e) =>
                      setTipoQuestao(e.target.value as QuestionType)
                    }
                    disabled={editMode}
                  >
                    <option value="objective">Objetiva</option>
                    <option value="dissertative">Dissertativa</option>
                    <option value="set">Textos em Comum</option>
                  </Form.Select>
                </div>
              </InputGroup>

              {/* Campo "Disciplina *" - desabilitado no modo de edição */}
              <InputGroup>
                <div className="flex fdcolumn flex1 margin-bottom">
                  <Form.Label>
                    Disciplina <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Form.Select
                    value={disciplina}
                    onChange={(e) => setDisciplina(e.target.value)}
                    disabled={editMode}
                  >
                    <option value="">Selecione uma Disciplina</option>
                    {discipline.map((disc: any) => (
                      <option key={disc.id} value={disc.id}>
                        {disc.name}
                      </option>
                    ))}
                  </Form.Select>
                  {attemptedSave && !disciplina && (
                    <CustomAlert variant="danger">
                      Campo obrigatório
                    </CustomAlert>
                  )}
                </div>
              </InputGroup>

              {/* Nível de Escolaridade e Dificuldade */}
              <div className="row">
                <InputGroup className="col-lg">
                  <div className="flex fdcolumn flex1 margin-bottom">
                    <Form.Label>
                      Nível de Escolaridade{" "}
                      <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={nivelEscolaridade}
                      onChange={(e) =>
                        setNivelEscolaridade(
                          e.target.value as QuestionEducationLevel
                        )
                      }
                    >
                      <option value="elementary">Fundamental</option>
                      <option value="high_school">Médio</option>
                      <option value="college">Superior</option>
                    </Form.Select>
                    {attemptedSave && !nivelEscolaridade && (
                      <CustomAlert variant="danger">
                        Campo obrigatório
                      </CustomAlert>
                    )}
                  </div>
                </InputGroup>
                <InputGroup className="col-lg">
                  <div className="flex fdcolumn flex1 margin-bottom">
                    <Form.Label>
                      Nível de Dificuldade{" "}
                      <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={nivelDificuldade}
                      onChange={(e) =>
                        setNivelDificuldade(
                          e.target.value as QuestionDifficultyLevel
                        )
                      }
                    >
                      <option value="easy">Fácil</option>
                      <option value="medium">Médio</option>
                      <option value="hard">Difícil</option>
                    </Form.Select>
                    {attemptedSave && !nivelDificuldade && (
                      <CustomAlert variant="danger">
                        Campo obrigatório
                      </CustomAlert>
                    )}
                  </div>
                </InputGroup>
              </div>

              {/* Tags */}
              <InputGroup>
                <div className="flex fdcolumn flex1 margin-bottom">
                  <TagsSelector
                    selectedTagIds={tags}
                    onChange={(selectedTagIds) => setTags(selectedTagIds)}
                  />
                </div>
              </InputGroup>

              {/* Exibe os editores somente se a disciplina estiver selecionada */}
              {disciplina ? (
                <>
                  <InputGroup className="mb-3">
                    <Form.Label>
                      {tipoQuestao === "set"
                        ? "Descrição do Conjunto"
                        : "Enunciado"}{" "}
                      <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <div className="w-100">
                      <JoditEditor
                        ref={enunciadoEditorRef}
                        value={enunciado || ""}
                        config={defaultEditorConfig}
                        onBlur={(content) => setEnunciado(content)}
                      />
                    </div>
                    {attemptedSave && enunciadoError && (
                      <Form.Text className="text-danger">
                        {enunciadoError}
                      </Form.Text>
                    )}
                  </InputGroup>

                  {/* No modo "Texto em Comum" a justificativa não é renderizada na questão principal */}
                  {tipoQuestao !== "set" && (
                    <InputGroup className="mb-3">
                      <Form.Label>Justificativa</Form.Label>
                      <div className="w-100">
                        <JoditEditor
                          ref={justificativaEditorRef}
                          value={justificativa || ""}
                          config={defaultEditorConfig}
                          onBlur={(content) => setJustificativa(content)}
                        />
                      </div>
                      {attemptedSave && justificativaError && (
                        <Form.Text className="text-danger">
                          {justificativaError}
                        </Form.Text>
                      )}
                    </InputGroup>
                  )}
                </>
              ) : (
                <div className="alert alert-warning" role="alert">
                  Selecione uma disciplina para continuar.
                </div>
              )}

              {/* Alternativas para questões objetivas (somente se disciplina estiver selecionada) */}
              {disciplina && tipoQuestao === "objective" && (
                <Card
                  style={{ border: "1px solid #ccc", padding: 10 }}
                  className="mb-3"
                >
                  <Card.Header>
                    <h5 className="mb-0">Alternativas</h5>
                  </Card.Header>
                  <Card.Body>
                    <Container>
                      {/* Linha contendo o seletor de quantidade e o botão de adicionar */}
                      <Row className="align-items-end mb-3">
                        <Col xs={6}>
                          <Form.Label>Quantidade:</Form.Label>
                          <Form.Select
                            value={alternativeCount}
                            onChange={(e) =>
                              setAlternativeCount(parseInt(e.target.value))
                            }
                          >
                            {Array.from({ length: 10 }, (_, i) => i + 1).map(
                              (num) => (
                                <option key={num} value={num}>
                                  {num} Alternativa{num > 1 ? "s" : ""}
                                </option>
                              )
                            )}
                          </Form.Select>
                        </Col>
                        <Col xs="auto">
                          <Button
                            variant="success"
                            onClick={() =>
                              handleAddMultipleAlternatives(alternativeCount)
                            }
                          >
                            <FaPlusSquare style={{ marginRight: 5 }} />
                            Adicionar
                          </Button>
                        </Col>
                      </Row>

                      {/* Listagem das alternativas adicionadas */}
                      {alternativas.map((alt, index) => (
                        <Card key={alt.localId} className="mb-2">
                          <Card.Body>
                            <Row>
                              <Col xs={12} className="mb-2">
                                <strong>
                                  {String.fromCharCode(97 + index)})
                                </strong>
                              </Col>
                              <Col xs={12}>
                                <JoditEditor
                                  value={alt.text || ""}
                                  config={defaultEditorConfig}
                                  onBlur={(content) =>
                                    setAlternativas((prev) =>
                                      prev.map((a) =>
                                        a.localId === alt.localId
                                          ? { ...a, text: content }
                                          : a
                                      )
                                    )
                                  }
                                />
                              </Col>
                              <Col
                                xs={12}
                                className="d-flex justify-content-between align-items-center mt-2"
                              >
                                <Form.Check
                                  type="checkbox"
                                  label="Correta?"
                                  checked={alt.isCorrect}
                                  onChange={(e) =>
                                    setAlternativas((prev) =>
                                      prev.map((a) =>
                                        a.localId === alt.localId
                                          ? {
                                              ...a,
                                              isCorrect: e.target.checked,
                                            }
                                          : { ...a, isCorrect: false }
                                      )
                                    )
                                  }
                                />
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() =>
                                    setAlternativas((prev) =>
                                      prev.filter(
                                        (item) => item.localId !== alt.localId
                                      )
                                    )
                                  }
                                >
                                  <MdDelete />
                                </Button>
                              </Col>

                              {/* Validações de erro ao salvar */}
                              {attemptedSave && alternativas.length === 0 && (
                                <div className="text-center">
                                  <CustomAlert variant="danger">
                                    Adicione pelo menos 1 alternativa
                                  </CustomAlert>
                                </div>
                              )}
                              {altError && (
                                <div className="text-center mt-2">
                                  <Form.Text className="text-danger">
                                    {altError}
                                  </Form.Text>
                                </div>
                              )}
                            </Row>
                          </Card.Body>
                        </Card>
                      ))}
                    </Container>
                  </Card.Body>
                </Card>
              )}

              {/* Subquestões para tipo "set" (somente se disciplina estiver selecionada) */}
              {disciplina && tipoQuestao === "set" && (
                <div className="p-3 border rounded bg-light">
                  <h5 className="fw-semibold">SubQuestões</h5>
                  <p className="text-muted">
                    Cada subquestão pode ser do tipo objetiva ou dissertativa.
                  </p>
                  <Button
                    variant="primary"
                    onClick={handleOpenModal}
                    className="mb-3"
                  >
                    <FaPlusSquare className="me-2" />
                    Adicionar SubQuestão
                  </Button>
                  {subQuestions.length > 0 ? (
                    <div className="table-responsive">
                      <Table hover className="rounded shadow-sm">
                        <thead className="bg-primary text-white">
                          <tr>
                            <th>Tipo</th>
                            <th>Enunciado</th>
                            <th
                              style={{ width: "120px" }}
                              className="text-center"
                            >
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {subQuestions.map((sq) => (
                            <tr key={sq.localId} className="align-middle">
                              <td>
                                <span
                                  className={`badge ${
                                    sq.type === "objective"
                                      ? "bg-success"
                                      : "bg-warning"
                                  } text-white`}
                                >
                                  {sq.type === "objective"
                                    ? "Objetiva"
                                    : "Dissertativa"}
                                </span>
                              </td>
                              <td>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizeInput(sq.statement),
                                  }}
                                />
                              </td>
                              <td className="text-center">
                                <div className="d-flex justify-content-center gap-2">
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={() => handleEditSubQuestion(sq)}
                                    style={{ width: "32px", height: "32px" }}
                                  >
                                    <BsFillEyeFill />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() =>
                                      handleDeleteSubQuestion(sq.localId)
                                    }
                                    style={{ width: "32px", height: "32px" }}
                                  >
                                    <MdDelete />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center text-muted mt-3">
                      <p className="mb-0">Nenhuma subquestão adicionada.</p>
                    </div>
                  )}
                  {attemptedSave && subQuestions.length === 0 && (
                    <CustomAlert variant="danger">
                      Adicione pelo menos 1 subquestão
                    </CustomAlert>
                  )}
                </div>
              )}

              <br />
              <div className="w100 inline-flex jcsb mt-3">
                <Button
                  variant="secondary"
                  onClick={() => navigate("/questions")}
                >
                  Voltar
                </Button>
                <Button
                  variant="success"
                  onClick={handleSaveAll}
                  disabled={isCreating || isUpdating}
                >
                  <SaveRounded style={{ marginRight: 5 }} />
                  {isCreating || isUpdating ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>

      {/* Modal de SubQuestão */}
      <SetSubQuestionModal
        show={showModal}
        onHide={handleCloseModal}
        onSave={handleSaveSubQuestion}
        editingData={editingSubQ}
      />

      {/* Modal de Questão Existente (erro 409) */}
      <QuestionModal
        show={showModalQuestion}
        onConfirm={handleConfirmCreate}
        onCancel={handleCancelModal}
      />
    </div>
  );
}

export default QuestionsForm;
